import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import useRole from "./crm/hooks/useRole";

/**
 * The ONE gate over every admin route OUTSIDE the CRM section (decision 5, role
 * matrix). Mounted as a single pathless layout route wrapping every sibling of `crm` in
 * BOTH route trees (routes/adminRoutes.jsx, tenant/TenantSite.jsx — text-identical line,
 * pinned by routes/__tests__/adminRouteParity.test.js), never as a per-page check: a
 * CRM helper's OTP session is refused server-side by every one of those pages already
 * (`server.require_admin` and its equivalents — `tenant_roles.resolve_actor`'s "anyone
 * else" branch, since a helper owns no tenant and is not a platform operator), so the
 * thirty-odd pages behind this gate would otherwise each independently discover their
 * own 403 and render whatever their own read-failure panel says, which is where a
 * helper actually landed before this gate existed (browser QA
 * test_reports/browser_qa/2026-09-30T00-43-29Z-crm-p3-helper and its diagnostic runs):
 * the full owner sidebar, a dead-end load-failure panel per page, and an inbox poll
 * that could never succeed. This component spares the round trip and sends the helper
 * straight to the one section the role matrix actually grants them.
 *
 * Redirects to `crm` (relative — a sibling of every route this wraps, on both hosts,
 * under whatever console base `adminHref` already resolved: `/admin/crm` or
 * `/t/{slug}/admin/crm`) for anyone `useRole` does not resolve to "owner". That
 * includes an absent/unrecognised `tenant_role` — `admin/crm/roles.js#normaliseRole`
 * fails closed to "helper" everywhere else in the CRM, and a route guard protecting the
 * FEWER-privilege side is exactly the place that direction has to hold, not the one
 * exception to it. `useRole` treats a platform operator god-viewing a designated
 * practice as "owner" (`tenant_roles.tenant_role_for`'s operator branch, mirrored from
 * `resolve_actor`), so a super-admin's own god-view session passes here unchanged.
 */
export default function RequireOwnerAdmin() {
  const { isOwner } = useRole();
  if (!isOwner) return <Navigate to="crm" replace />;
  return <Outlet />;
}
